import { IconButton } from "@material-ui/core";
import CreateIcon from "@material-ui/icons/Create";
import DeleteIcon from "@material-ui/icons/Delete";
import { useTranslations } from "adapters/i18n";
import { CustomTableColumn } from "features/shared/components";
import React, { useMemo } from "react";
import type { Corral, LivestockInfo, Retreat } from "shared/types";
import { CorralListTableColumn } from "../utils/corral.utils";

type UseCorralListTableProps = {
  isFormPanelOpen: boolean;
  livestockInfos: LivestockInfo[];
  onEditClick: (row: Corral) => void;
  onDeleteRequest: (row: Corral) => void;
  retreats: Retreat[];
};

export function useCorralListTable({
  isFormPanelOpen,
  livestockInfos,
  onDeleteRequest,
  onEditClick,
  retreats,
}: UseCorralListTableProps) {
  const { t } = useTranslations();

  const retreatNameMap = useMemo(
    () => new Map(retreats.map((retreat) => [retreat.id, retreat.name])),
    [retreats]
  );

  const infosByCorralIdMap = useMemo(() => {
    const map = new Map<number, LivestockInfo[]>();
    livestockInfos
      .filter((info) => info.endDate == null)
      .forEach((info) => {
        const infos = map.get(info.pastureId ?? -1) ?? [];
        infos.push(info);
        map.set(info.pastureId ?? -1, infos);
      });
    return map;
  }, [livestockInfos]);

  const columns = useMemo<
    CustomTableColumn<Corral, typeof CorralListTableColumn>[]
  >(
    () => [
      {
        id: CorralListTableColumn.Retreat,
        label: t("locales:retreat"),
        isSortable: true,
        type: "text",
        getRowValue: (row) => retreatNameMap.get(row.retreatId ?? -1) ?? null,
        renderCell: (value) => value ?? "-",
      },
      {
        id: CorralListTableColumn.Name,
        label: t("common:fields.name"),
        isSortable: true,
        type: "text",
        getRowValue: (row) => row.name,
      },
      {
        id: CorralListTableColumn.ShortName,
        label: t("common:fields.short-name"),
        isSortable: true,
        type: "text",
        getRowValue: (row) => row.shortName,
      },
      {
        id: CorralListTableColumn.AnimalCount,
        label: t("livestock:animals-amount"),
        isSortable: true,
        type: "number",
        getRowValue: (row) => {
          const infos = infosByCorralIdMap.get(row.id) ?? [];
          return infos.reduce((acc, info) => acc + info.quantity, 0);
        },
        renderCell: (value) => t("livestock:x-animals", { count: value ?? 0 }),
      },
      {
        id: CorralListTableColumn.Edit,
        label: t("common:actions.edit"),
        isSortable: false,
        type: "custom",
        isActionColumn: true,
        renderCell: (row) => (
          <EditCell
            isFormPanelOpen={isFormPanelOpen}
            onEditClick={onEditClick}
            row={row}
          />
        ),
      },
      {
        id: CorralListTableColumn.Delete,
        label: t("common:actions.delete"),
        isSortable: false,
        type: "custom",
        isActionColumn: true,
        renderCell: (row) => (
          <DeleteCell
            isFormPanelOpen={isFormPanelOpen}
            onDeleteRequest={onDeleteRequest}
            row={row}
          />
        ),
      },
    ],
    [
      infosByCorralIdMap,
      isFormPanelOpen,
      onDeleteRequest,
      onEditClick,
      retreatNameMap,
      t,
    ]
  );

  return { columns };
}

type EditCellProps = {
  isFormPanelOpen: boolean;
  onEditClick: (row: Corral) => void;
  row: Corral;
};

function EditCell({ isFormPanelOpen, onEditClick, row }: EditCellProps) {
  const { t } = useTranslations();

  const handleEditClick = () => {
    onEditClick(row);
  };

  return (
    <IconButton
      aria-label={t("common:actions.edit")}
      data-testid="edit-corral-button"
      disabled={isFormPanelOpen}
      onClick={handleEditClick}
    >
      <CreateIcon />
    </IconButton>
  );
}

type DeleteCellProps = {
  isFormPanelOpen: boolean;
  onDeleteRequest: (row: Corral) => void;
  row: Corral;
};

function DeleteCell({
  isFormPanelOpen,
  onDeleteRequest,
  row,
}: DeleteCellProps) {
  const { t } = useTranslations();

  const handleClick = () => {
    onDeleteRequest(row);
  };

  return (
    <IconButton
      aria-label={t("common:actions.delete")}
      data-testid="delete-corral-button"
      disabled={isFormPanelOpen}
      onClick={handleClick}
    >
      <DeleteIcon />
    </IconButton>
  );
}
